import classNames from 'classnames';

import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
  useAuth,
  usePhotographers,
  useScheduleCalendar,
  useAddAvailability,
  useRemoveAvailability,
  useAddUnavailability,
  useRemoveUnavailability,
  useLanguage,
  useAlert,
} from 'hooks';
import moment from 'moment';
import {
  TextField,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
} from '@material-ui/core';
import { HvSelect, HvButton, HvModal, HvLinkButton, HvPleaseWait } from 'components';
import { COLOR, MODAL, ENG, ALERT } from 'config/constants';
import { hideModal } from 'state/actions';
import {
  setModalType,
  setSelectedPhotographers,
  setEvents,
  setAvailabilityToBeDeleted,
  setSchedule,
} from 'state/slices/scheduleCalender';
import { LOADING } from 'state/constants';

interface Props {
  schedule?: any;
  photographerOptions?: any;
}
const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'left',
    color: COLOR.WHITE,
    padding: 20,
    borderRadius: 8,
    overflowY: 'hidden',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  control: {
    width: '100%',
    marginRight: 5,
    backgroundColor: COLOR.WHITE,
    borderRadius: 8,
  },
  selectDropdown: {
    overflow: 'hidden',
  },
  cancelButton: {
    backgroundColor: 'white',
    marginRight: 20,
    color: COLOR.LIGHT_BLUE,
    border: `1px solid ${COLOR.LIGHT_BLUE}`,
  },
  title: {
    fontFamily: 'FordAntenna-Medium',
    marginTop: 10,
  },
  shootingContent: {
    borderRadius: 8,
    border: `1px ${COLOR.LIGHT_BLUE} solid`,
    padding: 10,
    marginBottom: 10,
  },
  linkButton: {
    color: COLOR.WHITE,
    backgroundColor: COLOR.LIGHT_BLUE,
    borderRadius: 8,
  },
  detail: {
    marginBottom: 5,
  },
  modalContent: {
    minWidth: 400,
  },
});

function SchedulerModals({ schedule, photographerOptions }: Props) {
  const intl = useIntl();
  const classes = useStyles();
  const language = useLanguage();
  const { user, token } = useAuth();
  const dispatch = useDispatch();
  const { displayAlert } = useAlert();
  const { isAdmin } = user;
  const isEnglish = language === ENG;

  const {
    modalType,
    selectedPhotographers,
    events,
    availabilityToBeDeleted,
    unavailabilityToBeDeleted,
    shootsToDisplay,
  } = useScheduleCalendar(token);

  const { addingAvailability, addAvailability, addedAvailability, addedAvailabilityStatus } =
    useAddAvailability(token);
  const {
    removingAvailability,
    removeAvailability,
    removedAvailability,
    removedAvailabilityStatus,
  } = useRemoveAvailability(token);
  const {
    addingUnavailability,
    addUnavailability,
    addedUnavailability,
    addedUnavailabilityStatus,
  } = useAddUnavailability(token);
  const {
    removingUnavailability,
    removeUnavailability,
    removedUnavailability,
    removedUnavailabilityStatus,
  } = useRemoveUnavailability(token);
  const { photographers } = isAdmin ? usePhotographers(token) : { photographers: [] };

  const [selectedTechToRemoveAvailability, setSelectedTechToRemoveAvailability] = useState(null);
  const [selectedTechToAddUnavailability, setSelectedTechToAddUnavailability] = useState(null);
  const [reason, setReason] = useState(null);
  const [tech, setTech] = useState(null);

  const fmtAddAvailability = intl.formatMessage({ id: 'prodScheduler.addAvailability' });
  const fmtRemoveAvailability = intl.formatMessage({ id: 'prodScheduler.removeAvailability' });
  const fmtAddUnavailability = intl.formatMessage({ id: 'prodScheduler.addUnavailability' });
  const fmtRemoveUnavailability = intl.formatMessage({ id: 'prodScheduler.removeUnavailability' });
  const fmtShootings = intl.formatMessage({ id: 'shootings' });
  const fmtChoosePhotographer = intl.formatMessage({ id: 'prodScheduler.choosePhotographer' });
  const fmtConfirmAvailability = intl.formatMessage({ id: 'prodScheduler.confirmAvailability' });
  const fmtSave = intl.formatMessage({ id: 'prodDispatcher.save' });
  const fmtCancel = intl.formatMessage({ id: 'prodDispatcher.cancel' });
  const fmtDate = intl.formatMessage({ id: 'feedback.date' });
  const fmtName = intl.formatMessage({ id: 'feedback.name' });
  const fmtReason = intl.formatMessage({ id: 'prodScheduler.reason' });
  const fmtDelete = intl.formatMessage({ id: 'customShootingOffer.addNewShootingOffer.delete' });
  const fmtPhotogrphapher = intl.formatMessage({ id: 'postProd.photographer' });
  const fmtStart = intl.formatMessage({ id: 'prodScheduler.start' });
  const fmtEnd = intl.formatMessage({ id: 'prodScheduler.end' });
  const fmtBroker = intl.formatMessage({ id: 'menuTypes.BrokerTools' });
  const fmtExpectedTime = intl.formatMessage({ id: 'prodScheduler.scheduledTime' });
  const fmtShootingPackage = intl.formatMessage({ id: 'shooting.detail.package' });
  const fmtServiceOffers = intl.formatMessage({
    id: 'customShootingOffer.addNewShootingOffer.serviceOffers',
  });
  const fmtOtherDetails = intl.formatMessage({ id: 'prodScheduler.otherDetails' });
  const fmtRoomCount = intl.formatMessage({ id: 'prodScheduler.roomCount' });
  const fmtNote = intl.formatMessage({ id: 'postProd.note' });
  const fmtPhographer = intl.formatMessage({ id: 'postProd.photographer' });
  const fmtShootingTime = intl.formatMessage({ id: 'prodScheduler.shootingTime' });
  const fmtPingTime = intl.formatMessage({ id: 'prodScheduler.pingTime' });
  const fmtManageShootings = intl.formatMessage({ id: 'prodScheduler.manageShootings' });
  const fmtRemoveSuccess = intl.formatMessage({ id: 'prodScheduler.removeSuccess' });
  const fmtRemoveFail = intl.formatMessage({ id: 'prodScheduler.removeFail' });
  const fmtAddSuccess = intl.formatMessage({ id: 'prodScheduler.addSuccess' });
  const fmtAddFail = intl.formatMessage({ id: 'prodScheduler.addFail' });

  useEffect(() => {
    let tech;
    if (availabilityToBeDeleted) {
      if ('tech' in availabilityToBeDeleted) {
        tech = availabilityToBeDeleted.tech;
      } else if ('availability' in availabilityToBeDeleted) {
        tech = availabilityToBeDeleted.availability.user;
      } else if ('user' in availabilityToBeDeleted) {
        tech = availabilityToBeDeleted.user;
      }
      setTech(tech);
    }
  }, []);
  useEffect(() => {
    if (addedAvailability && addedAvailabilityStatus === 200) {
      displayAlert(ALERT.SUCCESS, fmtAddSuccess);
      dispatch(hideModal(MODAL.SCHEDULER_MODAL));
      dispatch(setModalType(''));
      dispatch(setSelectedPhotographers([]));
    } else if (addedAvailabilityStatus === 500) {
      displayAlert(ALERT.ERROR, fmtAddFail);
      dispatch(hideModal(MODAL.SCHEDULER_MODAL));
      dispatch(setModalType(''));
    }
  }, [addedAvailability, addedAvailabilityStatus]);
  useEffect(() => {
    if (removedAvailability && removedAvailabilityStatus === 200) {
      displayAlert(ALERT.SUCCESS, fmtRemoveSuccess);
      dispatch(hideModal(MODAL.SCHEDULER_MODAL));
      dispatch(setModalType(''));
    } else if (removedAvailabilityStatus === 500) {
      displayAlert(ALERT.ERROR, fmtRemoveFail);
      dispatch(hideModal(MODAL.SCHEDULER_MODAL));
      dispatch(setModalType(''));
    }
  }, [removedAvailability, removedAvailabilityStatus]);

  useEffect(() => {
    if (addedUnavailability && addedUnavailabilityStatus === 200) {
      displayAlert(ALERT.SUCCESS, fmtAddSuccess);
      dispatch(hideModal(MODAL.SCHEDULER_MODAL));
      dispatch(setModalType(''));
    } else if (addedUnavailabilityStatus === 500) {
      displayAlert(ALERT.ERROR, fmtAddFail);
      dispatch(hideModal(MODAL.SCHEDULER_MODAL));
      dispatch(setModalType(''));
    }
  }, [addedUnavailability, addedUnavailabilityStatus]);

  useEffect(() => {
    if (removedUnavailability && removedUnavailabilityStatus === 200) {
      displayAlert(ALERT.SUCCESS, fmtRemoveSuccess);
      dispatch(hideModal(MODAL.SCHEDULER_MODAL));
      dispatch(setModalType(''));
    } else if (removedUnavailabilityStatus === 500) {
      displayAlert(ALERT.ERROR, fmtRemoveFail);
      dispatch(hideModal(MODAL.SCHEDULER_MODAL));
      dispatch(setModalType(''));
    }
  }, [removedUnavailability, removedUnavailabilityStatus]);

  const onClose = () => {
    dispatch(hideModal(MODAL.SCHEDULER_MODAL));
    dispatch(setModalType(''));
    dispatch(setSelectedPhotographers([]));
    if (modalType === 'new-availability') {
      const newEvents = events.filter((event) => {
        return event.type !== 'new-availability';
      });
      dispatch(setEvents(newEvents));
    } else if (modalType === 'new-unavailability') {
      const newEvents = events.filter((event) => {
        return event.type !== 'new-unavailability';
      });
      dispatch(setEvents(newEvents));
    } else if (modalType === 'remove-availability') {
      dispatch(setAvailabilityToBeDeleted(null));
    }
  };
  const getTitle = () => {
    switch (modalType) {
      case 'new-availability':
        return fmtAddAvailability;
      case 'remove-availability':
        return fmtRemoveAvailability;
      case 'new-unavailability':
        return fmtAddUnavailability;
      case 'remove-unavailability':
        return fmtRemoveUnavailability;
      case 'display-shootings':
        return fmtShootings;
      default:
        return '';
    }
  };
  const handleClick = (photographer) => {
    let newSelectedPhotographers = [];
    if (selectedPhotographers.indexOf(photographer.id) === -1) {
      newSelectedPhotographers = selectedPhotographers.concat(photographer.id);
    } else {
      newSelectedPhotographers = selectedPhotographers.filter((s) => {
        return s !== photographer.id;
      });
    }
    dispatch(setSelectedPhotographers(newSelectedPhotographers));
  };

  const saveNewAvailability = async () => {
    const userIds = isAdmin ? selectedPhotographers : [user.id];
    let selectedTechsObj;
    if (isAdmin) {
      selectedTechsObj = photographers.filter((photographer) => {
        return userIds.indexOf(photographer.id) !== -1;
      });
    } else {
      selectedTechsObj = [user];
    }
    const availability = events.find((event) => {
      return event.type === 'new-availability';
    });
    if (!availability) {
      return;
    }
    const dates = [];
    let start = moment(availability.start);
    const end = moment(availability.end);
    while (start.isBefore(end)) {
      dates.push(start.format('YYYY-MM-DD'));
      start = start.add(1, 'day');
    }
    const res = await addAvailability(token, userIds, selectedTechsObj, dates);
  };

  const removeAvailabilityForTech = async () => {
    if (isAdmin && !selectedTechToRemoveAvailability) {
      return;
    }
    let techId = selectedTechToRemoveAvailability;
    if (!isAdmin) {
      techId = tech.id;
    }
    removeAvailability(
      token,
      techId,
      moment(availabilityToBeDeleted.start).format('YYYY-MM-DD'),
      availabilityToBeDeleted.availability.id,
    );
  };

  const saveNewUnavailability = async () => {
    const unavailability = events.find((event) => {
      return event.type === 'new-unavailability';
    });
    if (!unavailability) {
      return;
    }
    const start = moment(unavailability.start).utc().second(0).format();
    const end = moment(unavailability.end).utc().second(0).format();
    const unavailability_id = null;
    const foundSelectedTech = photographers.find((photographer) => {
      return photographer.id === selectedTechToAddUnavailability;
    });
    const selectedTech = isAdmin ? foundSelectedTech : user;
    const res = await addUnavailability(selectedTech, start, end, unavailability_id, reason);
  };

  const removeUnavailabilityForTech = async () => {
    const res = await removeUnavailability(unavailabilityToBeDeleted.id);
  };

  const newAvailabilityContent = () => {
    const event = events.find((event) => {
      return event.type === 'new-availability';
    });
    return isAdmin ? (
      <div>
        <Typography>{fmtChoosePhotographer}</Typography>
        <List>
          {photographers.map((photographer) => {
            return (
              <ListItem
                key={photographer.id}
                dense
                button
                onClick={() => {
                  return handleClick(photographer);
                }}
              >
                <ListItemIcon>
                  <Checkbox
                    edge='start'
                    checked={selectedPhotographers.indexOf(photographer.id) !== -1}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary={`Tech Name - ${photographer.fullName}`} />
              </ListItem>
            );
          })}
        </List>
      </div>
    ) : (
      <div>
        <Typography>{fmtConfirmAvailability}</Typography>
        <Typography>
          {`${moment(event.start).format('dddd, MMMM Do YYYY')} - ${moment(event.end).format(
            'dddd, MMMM Do YYYY',
          )}`}
        </Typography>
      </div>
    );
  };

  const removeAvailabilityContent = () => {
    if (!tech) return null;
    return (
      <div>
        <Typography>
          {`${fmtDate} - ${moment(availabilityToBeDeleted.start).format('YYYY-MM-DD')}`}
        </Typography>
        {isAdmin ? (
          <ListItem
            key={tech.id}
            dense
            button
            onClick={() => {
              if (selectedTechToRemoveAvailability === tech.id) {
                setSelectedTechToRemoveAvailability(null);
              } else {
                setSelectedTechToRemoveAvailability(tech.id);
              }
            }}
          >
            <ListItemIcon>
              <Checkbox
                edge='start'
                checked={selectedTechToRemoveAvailability === tech.id}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
            <ListItemText primary={`${fmtName} - ${tech?.firstname} ${tech?.lastname}`} />
          </ListItem>
        ) : null}
      </div>
    );
  };

  const newUnavailabilityContent = () => {
    const event = events.find((event) => {
      return event.type === 'new-unavailability';
    });
    return (
      <div>
        {isAdmin ? (
          <div>
            <Typography>{fmtChoosePhotographer}</Typography>
            <HvSelect
              classes={{ rootClass: classes.control, dropdownClass: classes.selectDropdown }}
              options={photographerOptions}
              value={selectedTechToAddUnavailability}
              onChange={(value) => {
                setSelectedTechToAddUnavailability(value);
              }}
            />
          </div>
        ) : (
          <div>
            <Typography>
              {`${moment(event.start).second(0).format('dddd, MMMM Do YYYY, h:mm:ss a')} - ${moment(
                event.end,
              )
                .second(0)
                .format('dddd, MMMM Do YYYY, h:mm:ss a')}`}
            </Typography>
          </div>
        )}
        <TextField
          label={fmtReason}
          multiline
          variant='outlined'
          maxRows={5}
          fullWidth
          value={reason}
          margin='normal'
          onChange={(e) => {
            setReason(e.target.value);
          }}
        />
      </div>
    );
  };

  const removeUnavailabilityContent = () => {
    return (
      <div>
        <Typography className={classes.detail}>
          {`${fmtPhotogrphapher}: ${unavailabilityToBeDeleted.tech.firstname} ${unavailabilityToBeDeleted.tech.lastname}`}
        </Typography>
        <Typography className={classes.detail}>
          {`${fmtStart}: ${moment(unavailabilityToBeDeleted.start).format(
            'dddd, MMMM Do YYYY, h:mm:ss a',
          )}`}
        </Typography>
        <Typography className={classes.detail}>
          {`${fmtEnd}: ${moment(unavailabilityToBeDeleted.end).format(
            'dddd, MMMM Do YYYY, h:mm:ss a',
          )}`}
        </Typography>
        {unavailabilityToBeDeleted.summary ? (
          <Typography className={classes.detail}>
            {`${fmtReason}: ${unavailabilityToBeDeleted.summary}`}
          </Typography>
        ) : null}
      </div>
    );
  };

  const shootingsContent = () => {
    return (
      <div>
        <div className={classes.row}>
          <Typography className={classes.title}>
            {moment(shootsToDisplay[0].scheduleddate).format('YYYY-MM-DD')}
          </Typography>
          <HvLinkButton
            className={classes.linkButton}
            to={`/prod-dispatcher?date=${moment(shootsToDisplay[0].scheduleddate).format(
              'YYYY-MM-DD',
            )}`}
            target='_blank'
          >
            <Typography>{fmtManageShootings}</Typography>
          </HvLinkButton>
        </div>
        {shootsToDisplay.map((shoot) => {
          const { listing, service_offers, room_count, note, tech, ping_time, starttime, endtime } =
            shoot;
          const shootStartTime = moment(shoot.scheduleddate).format('hh:mm a');
          const shootEndTime = moment(shoot.scheduleddate)
            .add(shoot.estimated_duration || 0, 'seconds')
            .format('hh:mm a');
          const pingTime = moment(shoot.ping_time).format('hh:mm a');
          const startTime = moment(shoot.starttime).format('hh:mm a');
          const endTime = moment(shoot.endtime).format('hh:mm a');
          let totalTimeSeconds;

          if (shoot.ping_time != null) {
            totalTimeSeconds = moment(shoot.endtime).diff(moment(shoot.ping_time), 'seconds');
          } else {
            totalTimeSeconds = moment(shoot.endtime).diff(moment(shoot.starttime), 'seconds');
          }
          const totalTime = moment('2015-01-01')
            .startOf('day')
            .seconds(totalTimeSeconds)
            .format('H:mm');

          const getServiceOffers = () => {
            if (service_offers && service_offers.length > 0) {
              return (
                <div>
                  <Typography className={classes.title}>{`${fmtServiceOffers}: `}</Typography>
                  {service_offers.map((so) => {
                    return (
                      <Typography key={so.id}>
                        {isEnglish ? so.serviceOffer.display_name : so.serviceOffer.display_name_fr}
                      </Typography>
                    );
                  })}
                </div>
              );
            }
            return null;
          };
          return (
            <div className={classes.shootingContent}>
              <Typography className={classes.title}>
                {`${listing.app ? `#${listing.app}, ` : ''} ${listing.address}, ${listing.city},
                    ${listing.postalcode}`}
              </Typography>
              <Typography>{`${fmtBroker}: ${listing.broker.firstname} ${listing.broker.lastname}`}</Typography>
              <Typography>{`${fmtExpectedTime}: ${shootStartTime} - ${shootEndTime}`}</Typography>
              {shoot.shooting_params && shoot.shooting_params.shooting_package ? (
                <Typography>
                  {`${fmtShootingPackage}: ${
                    isEnglish
                      ? shoot.shooting_params.shooting_package.display_name_en
                      : shoot.shooting_params.shooting_package.display_name_fr
                  } (${shoot.shooting_params.shooting_package.hdr_photo_count} photos)`}
                </Typography>
              ) : null}
              {getServiceOffers()}
              <Typography className={classes.title}>{`${fmtOtherDetails}: `}</Typography>
              <Typography>{`${fmtRoomCount}: ${room_count}`}</Typography>
              {note ? <Typography>{`${fmtNote}: ${note}`}</Typography> : null}
              {tech ? (
                <Typography>{`${fmtPhographer}: ${tech.firstname} ${tech.lastname}`}</Typography>
              ) : null}
              {ping_time && starttime && endtime ? (
                <div>
                  <Typography>{`${fmtShootingTime}: ${totalTime}`}</Typography>
                  <Typography>{`${fmtPingTime}: ${pingTime}`}</Typography>
                  <Typography>{`${fmtStart}: ${startTime} --- ${fmtEnd}: ${endTime}`}</Typography>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  const modalContent = () => {
    switch (modalType) {
      case 'new-availability':
        return newAvailabilityContent();
      case 'remove-availability':
        return removeAvailabilityContent();
      case 'new-unavailability':
        return newUnavailabilityContent();
      case 'remove-unavailability':
        return removeUnavailabilityContent();
      case 'display-shootings':
        return shootingsContent();
      default:
        return '';
    }
  };

  const getButtons = () => {
    switch (modalType) {
      case 'new-availability':
        return (
          <>
            <HvButton
              key='cancel'
              className={classes.cancelButton}
              onClick={() => {
                return onClose();
              }}
            >
              {fmtCancel}
            </HvButton>
            <HvButton key='newAvailability' onClick={saveNewAvailability}>
              {isAdmin ? `${fmtSave} (${selectedPhotographers.length})` : fmtSave}
            </HvButton>
          </>
        );
      case 'remove-availability':
        return [
          <HvButton
            key='cancel'
            className={classes.cancelButton}
            onClick={() => {
              return onClose();
            }}
          >
            {fmtCancel}
          </HvButton>,
          <HvButton
            key='removeAvailability'
            disabled={isAdmin && !selectedTechToRemoveAvailability}
            onClick={() => {
              return removeAvailabilityForTech();
            }}
          >
            {fmtRemoveAvailability}
          </HvButton>,
        ];
      case 'new-unavailability':
        return [
          <HvButton
            key='cancel'
            className={classes.cancelButton}
            onClick={() => {
              return onClose();
            }}
          >
            {fmtCancel}
          </HvButton>,
          <HvButton
            key='newUnavailability'
            disabled={isAdmin && !selectedTechToAddUnavailability}
            onClick={saveNewUnavailability}
          >
            {fmtSave}
          </HvButton>,
        ];
      case 'remove-unavailability':
        return [
          <HvButton
            key='cancel'
            className={classes.cancelButton}
            onClick={() => {
              return onClose();
            }}
          >
            {fmtCancel}
          </HvButton>,
          <HvButton
            key='removeUnavailability'
            onClick={() => {
              return removeUnavailabilityForTech();
            }}
          >
            {fmtDelete}
          </HvButton>,
        ];
      default:
        return [];
    }
  };
  if (
    [
      addingAvailability,
      removingAvailability,
      addingUnavailability,
      removingUnavailability,
    ].includes(LOADING.STARTED)
  ) {
    return <HvPleaseWait />;
  }
  return (
    <HvModal title={getTitle()} buttons={getButtons()} onClose={onClose}>
      <div className={classes.modalContent}>{modalContent()}</div>
    </HvModal>
  );
}

export default SchedulerModals;
